import { Box, Button, Stack } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { usePostCreditMemoMutation } from '../../../../../api/front-office/roomposting.api.rtk';
import LoadingComponent from '../../../../../components/animation/loading-component';
import useToast from '../../../../../hooks/useToast';
import { useRoomPostingContext } from '../../../containers/fo-room_posting.container';
import { RoomPostingCreditMemoIE } from '../../../interface/room-posting-interface';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import useDisclosure from '../../../../../hooks/useDisclosure';
import { formatNumber } from '../../../../../utils/format';
import { creditMemoSchema } from '../../../interface/room-posting-schema-interface';
import CreditMemoVoucher from '../voucher/fo-room-posting-cm-voucher';

interface FormValues {
  itemRemarks: string;
  credit_amount: number;
  guest_name: string;
}

interface CreditMemoInfoIE {
  selectedFolioUUID: string;
  guestId: string | number;
  guestName: string;
  creditAmount: number;
  cutoffDate: string;
  moduleId: string | number;
  subGroupId: string | number;
}

const FORoomPostingCreditMemo = ({
  creditMemoDetails,
  handleClose,
  handleCheckout,
}: {
  creditMemoDetails: CreditMemoInfoIE;
  handleClose: () => void;
  handleCheckout: () => void;
}) => {
  const { toast } = useToast();
  const { useProperty, ledgerRefetch } = useRoomPostingContext();

  //CAS  Voucher
  const printCMDisclosure = useDisclosure();
  // API Posting CAS
  //const [postCASTransactionAPI] = usePostCASTransactionMutation();
  const [CMno, setCMno] = useState<number>(0);

  // API Modify Item Line Remarks
  const [postCMTrans, { isLoading: isFetching }] = usePostCreditMemoMutation();

  const methods = useForm<FormValues>({
    resolver: yupResolver(creditMemoSchema),
    mode: 'onSubmit',
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
    // register,
    // setError,
    setValue,
    watch,
    // reset,
  } = methods;

  useEffect(() => {
    setValue('credit_amount', creditMemoDetails?.creditAmount);
    setValue('guest_name', creditMemoDetails?.guestName);
  }, []);
  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    const { itemRemarks } = data;
    const postCreditMemo: RoomPostingCreditMemoIE = {
      properties: [
        {
          property: {
            propertyID: useProperty,
            cm_transaction: [
              {
                transaction_type: 'NEW',
                transaction_identifier: creditMemoDetails?.selectedFolioUUID,
                cutoff_date: creditMemoDetails?.cutoffDate,
                module_id: creditMemoDetails?.moduleId,
                sub_group_id: creditMemoDetails?.subGroupId,
                remarks: itemRemarks,
                guest_id: creditMemoDetails?.guestId,
                guest_name: creditMemoDetails?.guestName,
                credit_amount: creditMemoDetails?.creditAmount,
                cm_id: 0,
              },
            ],
          },
        },
      ],
    };

    await postCMTrans(postCreditMemo)
      .unwrap()
      .then((cm_result) => {
        toast({
          severity: 'success',
          message: `Successfully apply credit memo.`,
        });

        if (cm_result?.data?.[0]?.result) {
          setCMno(cm_result?.data?.[0]?.result);
          printCMDisclosure.onOpen();
        } else {
          //close
          toast({
            severity: 'error',
            message: `Invalid Credit Memo Slip`,
          });
          handleClose();
          handleCheckout();
        }
        //  reset();
      })
      .catch((error) =>
        toast({
          severity: 'error',
          message: `Error occurred apply credit memo: ${error?.remarks?.message}`,
        }),
      );
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{ minWidth: '370px' }}>
        {isFetching && <LoadingComponent />}
        <Stack component='form' onSubmit={handleSubmit(onSubmit)}>
          <Stack
            sx={{ p: 3, borderBottom: '1px dashed lightgray' }}
            gap='5px'
            width='100%'
          >
            <InputFieldsController
              label='Guest Name'
              name='guest_name'
              control={control}
              errors={errors}
              value={creditMemoDetails?.guestName}
            />
            <InputFieldsController
              label='Credit Amount'
              name='credit_amount'
              control={control}
              errors={errors}
              value={formatNumber(creditMemoDetails?.creditAmount, 2, 2)}
              onChange={() => {
                setValue('credit_amount', creditMemoDetails?.creditAmount);
              }}
            />

            <InputFieldsController
              label='Remarks'
              name='itemRemarks'
              placeholder='Enter credit memo remarks'
              control={control}
              errors={errors}
              multiline
              rows={4}
              helperText={`${watch('itemRemarks')?.length || 0}/100`}
              wrapperStyle={{ mb: 0 }}
            />
          </Stack>
          <Stack alignItems='flex-end' p={3}>
            <Stack direction='row' gap='5px'>
              <Button
                sx={{
                  width: '75px',
                  height: '35px',
                  border: '1px solid',
                  backgroundColor: 'transparent',
                }}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                sx={{ height: '35px', width: '80px' }}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <CreditMemoVoucher
          cm_no={CMno}
          isOpen={printCMDisclosure.isOpen}
          onClose={() => {
            printCMDisclosure.onClose();
            ledgerRefetch();
            handleClose();
            handleCheckout();
          }}
        />
      </Box>
    </FormProvider>
  );
};

export default memo(FORoomPostingCreditMemo);
